import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Default franchise config (fallback only)
const DEFAULT_FRANCHISE = {
  slug: "naperville-elmhurst",
  name: "Rolling Suds of Naperville - Elmhurst",
  display_name: "Naperville - Elmhurst",
  logo_url: "https://customer-assets.emergentagent.com/job_instaquote-cleaning/artifacts/damu7ge2_Logo%20InstaQuote.png",
  contact: {
    phone: "(630) 448-7014",
    email: "anelie.pauig@rollingsuds.com",
    website: "www.rollingsudspowerwashing.com/naperville-elmhurst"
  },
  branding: {
    primary_color: "#1e40af",
    secondary_color: "#10b981",
    hero_image_url: null,
    tagline: "Exterior Cleaning Made Simple"
  },
  service_area_zips: [
    "60101", "60104", "60119", "60124", "60126", "60130", "60134", "60137",
    "60139", "60144", "60147", "60148", "60151", "60153", "60154", "60155",
    "60162", "60163", "60174", "60175", "60181", "60183", "60185", "60187",
    "60188", "60189", "60190", "60191", "60301", "60302", "60303", "60304",
    "60402", "60455", "60457", "60458", "60459", "60499", "60501", "60440", 
    "60490", "60502", "60503", "60504", "60505", "60506", "60510", "60511", 
    "60512", "60514", "60515", "60516", "60517", "60521", "60523", "60526", 
    "60532", "60538", "60540", "60542", "60543", "60545", "60554", "60555", 
    "60558", "60559", "60560", "60563", "60564", "60565", "60605", "60606", 
    "60629", "60632", "60638", "60804"
  ],
  is_active: true
};

const FranchiseContext = createContext(null);

export function FranchiseProvider({ children }) {
  const { franchiseSlug } = useParams();
  const [franchise, setFranchise] = useState(DEFAULT_FRANCHISE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFranchise = async () => {
      const slug = franchiseSlug || "naperville-elmhurst";
      
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/franchise/${slug}`);
        // Merge with defaults to ensure all fields exist
        const loaded = {
          ...DEFAULT_FRANCHISE,
          ...response.data,
          contact: { ...DEFAULT_FRANCHISE.contact, ...response.data.contact },
          branding: { ...DEFAULT_FRANCHISE.branding, ...response.data.branding }
        };
        setFranchise(loaded);
        setError(null);
      } catch {
        if (slug === "naperville-elmhurst") {
          // Use default for main franchise
          setFranchise(DEFAULT_FRANCHISE);
          setError(null);
        } else {
          setError("Franchise not found");
          setFranchise(DEFAULT_FRANCHISE);
        }
      } finally {
        setLoading(false);
      }
    };

    loadFranchise();
  }, [franchiseSlug]);

  // Helper to check if ZIP is in service area
  const isInServiceArea = useCallback((zipCode) => {
    return franchise?.service_area_zips?.includes(zipCode) || false;
  }, [franchise?.service_area_zips]);

  // Helper to get quote URL for this franchise
  const getQuoteUrl = useCallback(() => {
    if (!franchiseSlug || franchiseSlug === "naperville-elmhurst") {
      return "/quote";
    }
    return `/${franchiseSlug}/quote`;
  }, [franchiseSlug]);

  // Helper to get home URL for this franchise
  const getHomeUrl = useCallback(() => {
    if (!franchiseSlug || franchiseSlug === "naperville-elmhurst") {
      return "/";
    }
    return `/${franchiseSlug}`;
  }, [franchiseSlug]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    franchise,
    loading,
    error,
    isInServiceArea,
    getQuoteUrl,
    getHomeUrl,
    franchiseSlug: franchiseSlug || "naperville-elmhurst"
  }), [franchise, loading, error, isInServiceArea, getQuoteUrl, getHomeUrl, franchiseSlug]);

  return (
    <FranchiseContext.Provider value={contextValue}>
      {children}
    </FranchiseContext.Provider>
  );
}

export function useFranchise() {
  const context = useContext(FranchiseContext);
  if (!context) {
    return {
      franchise: DEFAULT_FRANCHISE,
      loading: false,
      error: null,
      isInServiceArea: (zip) => DEFAULT_FRANCHISE.service_area_zips.includes(zip),
      getQuoteUrl: () => "/quote",
      getHomeUrl: () => "/",
      franchiseSlug: "naperville-elmhurst"
    };
  }
  return context;
}

export default FranchiseContext;
